/* eslint-disable no-debugger */
import { AnyAction, Dispatch, Middleware, MiddlewareAPI } from 'redux';
import _ from 'lodash';
import ActionTypes from '../../../../constants/ActionTypes';
import ApiUrls from '../../../../constants/ApiUrls';
import MiddlewareEntities from '../../../../constants/MiddlewareEntities';
import ProductDataFactory from '../../../../factories/product/ProductDataFactory';
import ProductFactory from '../../../../factories/product/ProductFactory';
import BusinessRuleFacade from '../../../../rules/BusinessRuleFacade';
import TariffPackageService from '../../../../services/TariffPackageService';
import { mapSubsidyContractByUrlParam } from '../../../../utilites/util';
import ProductValidator from '../../../../validators/ProductValidator';
import { apiRequest } from '../../api/actions';
import { createErrorNotification, getNotification } from '../../notification/actions';
import { setPageDataProductOverviewAction } from '../../pageData/actions';
import { setErrorModeAction } from '../../redirection/actions';
import {
  createSearchParamsProductOverviewAction,
  setSearchParamsProductOverviewAction,
} from '../../searchParams/actions';
import {
  setProductAction,
  enrichProductAction,
  getProductDataAction,
  setProductDataAction,
  setCosmosInstallmentsAction,
  setContractAction,
  setCosmosModelAction,
  setInstallmentsOverBillPeriodAction,
  setColorAction,
  setTariffAction,
  setPurchaseAction,
  setParticipation,
  productValidatorAction,
  setPaymentTypeAction,
  setContractTypeAction,
} from './actions';
import { setLoader } from '../../ui/actions';
import UrlService from '../../../../services/UrlService';
import { getSessionData } from '../../sessionData/actions';
import { wishListProductChangeAction } from '../../wishList/actions';
import LocalStorageKeys from '../../../../constants/LocalStorageKeys';
import { getPreBasket } from '../../preBasket/actions';
import { clearAdditionalProducts } from '../../../../utilites/additionalProductsUtils';
import { deviceTypes } from '../../../../constants/TelcoDeviceTypes';
import { getProductSuggestions } from '../suggestions/actions';

const businessRuleFacade = BusinessRuleFacade.getInstance();

export const getProductMiddleware: Middleware = (store: MiddlewareAPI) => (next: Dispatch) => (action: AnyAction) => {
  next(action);
  switch (action.type) {
  case ActionTypes.PRODUCT.FETCH:
    next(
      apiRequest(
        null,
        'GET',
        `${ApiUrls.PRODUCTS}/${action.params.manufacturer}/${action.params.productName}`,
        {
          lang: action.params.lang,
          section: action.params.section,
        },
        null,
        MiddlewareEntities.PRODUCT,
        {}
      )
    );
    next(setLoader(true, MiddlewareEntities.PRODUCT));
    break;
  case `${MiddlewareEntities.PRODUCT} ${ActionTypes.API_SUCCESS}`:
    const params = UrlService.getParamsFromUrlOnLoadPage();
    const savedWishListProduct = JSON.parse(localStorage.getItem(LocalStorageKeys.SAVED_WISH_LIST_PRODUCTS) ?? '{}');

    next(setSearchParamsProductOverviewAction({ ...params }));
    next(productValidatorAction(action.payload.data.product));
    next(setPageDataProductOverviewAction(action.payload.data.pageData));
    next(getSessionData(action.payload.data.session));
    next(getNotification(action.payload.data.session.flashMessages, MiddlewareEntities.PRODUCTS));
    next(getProductDataAction(action.payload.data.pageData, action.payload.data.product));
    next(setLoader(false, MiddlewareEntities.PRODUCT));
    if (store.getState().session.user.isLoggedIn && !_.isEmpty(savedWishListProduct)) {
      next(
        wishListProductChangeAction(
          savedWishListProduct.status,
          savedWishListProduct.productId,
          savedWishListProduct.lang,
          savedWishListProduct.section,
          savedWishListProduct.type
        )
      );
    }
    businessRuleFacade.init(action.payload.data.product, {});
    if (businessRuleFacade.isTvOrCordlessPhoneOrStb()) {
      next(getPreBasket());
    }
    const productDataHasSuggestedProducts = deviceTypes.includes(
      action.payload.data.product.productLine.productLine
    );
    if (productDataHasSuggestedProducts) {
      const section = store.getState().section.section;
      const language = store.getState().language.language;
      next(
        getProductSuggestions({
          section: section,
          manufacturer: action.payload.data.product.manufacturer.name,
          productName: action.payload.data.product.model,
          lang: language,
        })
      );
    }

    clearAdditionalProducts();
    break;
  case `${MiddlewareEntities.PRODUCT} ${ActionTypes.API_ERROR}`:
    next(setLoader(false, MiddlewareEntities.PRODUCT));
    if (action.payload.data.status !== 404) {
      next(createErrorNotification(action.payload.data, MiddlewareEntities.PRODUCT));
    }
    break;
  }
};

export const productValidatorMiddleware: Middleware =
  (store: MiddlewareAPI) => (next: Dispatch) => (action: AnyAction) => {
    next(action);

    if (action.type.includes(ActionTypes.PRODUCT.PRODUCT_VALIDATOR)) {
      if (ProductValidator.validate(action.payload.product)) {
        return store.dispatch(enrichProductAction(action.payload.product));
      }

      return next(setErrorModeAction());
    }
  };

export const enrichProductMiddleware: Middleware =
  (store: MiddlewareAPI) => (next: Dispatch) => (action: AnyAction) => {
    next(action);

    if (action.type === ActionTypes.PRODUCT.ENRICH) {
      const { product } = action.payload;

      const productEntity = ProductFactory.createFromResponse(product);

      next(setProductAction(productEntity));
    }
  };

export const getProductDataFromUrlMiddleware: Middleware =
  (_: MiddlewareAPI) => (next: Dispatch) => (action: AnyAction) => {
    next(action);
    if (action.type === ActionTypes.PRODUCT.PRODUCT_DATA.GET_PRODUCT_DATA) {
      const productDataFactory = new ProductDataFactory(action.payload.product, action.payload.pageData);
      const productData = productDataFactory.create();
      next(setProductDataAction(productData));
    }
  };

export const getProductDataFromLocalStorageMiddleware: Middleware =
  (store: MiddlewareAPI) => (next: Dispatch) => (action: AnyAction) => {
    next(action);
    if (action.type === ActionTypes.PRODUCT.PRODUCT_DATA.GET_PRODUCT_DATA_FROM_LOCAL_STORAGE) {
      const { productData, product } = store.getState().productData;
      const hasExistingProductDataInState = Boolean(store.getState().productData.productData?.type);
      const hasExistingItemDataInState = Boolean(store.getState().productData.product?.item?.id);

      if (productData && !hasExistingProductDataInState) next(setProductDataAction(productData));
      if (product?.item && !hasExistingItemDataInState) next(setProductAction(product.item));
    }
  };

export const setProductDataMiddleware: Middleware =
  (store: MiddlewareAPI) => (next: Dispatch) => (action: AnyAction) => {
    next(action);

    switch (action.type) {
    case ActionTypes.PRODUCT.PRODUCT_DATA.SET_COSMOS_INSTALLMENTS:
      next(createSearchParamsProductOverviewAction({ cosmosInstallments: action.payload.cosmosInstallments }));
      next(setCosmosInstallmentsAction(action.payload.cosmosInstallments));
      break;
    case ActionTypes.PRODUCT.PRODUCT_DATA.SET_CONTRACT_OBLIGATION:
      next(createSearchParamsProductOverviewAction({ contract: action.payload.contract }));
      store.dispatch(
        getProductDataAction(store.getState().pageData.productOverview, store.getState().productData.product.item)
      );
      next(setContractAction(Number(mapSubsidyContractByUrlParam(action.payload.contract))));
      break;
    case ActionTypes.PRODUCT.PRODUCT_DATA.SET_COSMOS_MODEL:
      next(createSearchParamsProductOverviewAction({ cosmosModel: action.payload.cosmosModel }));
      next(setCosmosModelAction(action.payload.cosmosModel));
      break;
    case ActionTypes.PRODUCT.PRODUCT_DATA.SET_PAYMENT_TYPE:
      next(createSearchParamsProductOverviewAction({ paymentType: action.payload.paymentType }));
      next(setPaymentTypeAction(action.payload.paymentType));
      break;
    case ActionTypes.PRODUCT.PRODUCT_DATA.SET_INSTALLMENTS_OVER_BILL_PERIOD:
      next(createSearchParamsProductOverviewAction({ installments: action.payload.installmentsOverBillPeriod }));
      next(setInstallmentsOverBillPeriodAction(action.payload.installmentsOverBillPeriod));
      break;
    case ActionTypes.PRODUCT.PRODUCT_DATA.SET_COLOR:
      next(createSearchParamsProductOverviewAction({ color: action.payload.color }));
      next(setColorAction(action.payload.color));
      break;
    case ActionTypes.PRODUCT.PRODUCT_DATA.SET_TP_ID:
      next(createSearchParamsProductOverviewAction({ tariff: action.payload.tariff }));
      next(setTariffAction(action.payload.tariff));
      break;
    case ActionTypes.PRODUCT.PRODUCT_DATA.SET_PURCHASE:
      next(setPurchaseAction(action.payload.purchase));
      next(createSearchParamsProductOverviewAction({ purchase: action.payload.purchase }));
      const product = store.getState().productData.product.item;
      businessRuleFacade.init(product, {});
      if (!businessRuleFacade.isAccessory() && !businessRuleFacade.isTvOrCordlessPhoneOrStb()) {
        const tariffPackage = TariffPackageService.getTpByTpId(
          product,
          store.getState().searchParams.productOverview.tariff,
          store.getState().searchParams.productOverview.purchase
        );
        store.dispatch(setTariffAction(tariffPackage.id));
      }
      break;
    case ActionTypes.PRODUCT.PRODUCT_DATA.SET_PARTICIPATION:
      next(setParticipation(action.payload.participation));
      break;
    case ActionTypes.PRODUCT.PRODUCT_DATA.SET_CONTRACT_TYPE:
      next(setContractTypeAction(action.payload.contractType));
      next(createSearchParamsProductOverviewAction({ contractType: action.payload.contractType }));
      break;
    }
  };

export const productMiddleware = [
  getProductMiddleware,
  enrichProductMiddleware,
  productValidatorMiddleware,
  getProductDataFromUrlMiddleware,
  getProductDataFromLocalStorageMiddleware,
  setProductDataMiddleware,
];
